// Sources filters (KAL-916): status chips + owner, read from and written to the
// URL through the portal's `f_` codec (src/lib/filters.ts), so a filtered view is
// a link. Client-side only: it narrows the `/sources` payload already fetched and
// computes nothing C2 did not say.

import { applyFilters, parseFilters, type FilterValues } from "@/lib/filters";

import type { HealthState, Source } from "./api";
import { personLabel } from "./format";

/** Chip order, as the summary strip reads. */
export const FILTER_STATES: HealthState[] = ["ok", "drifting", "dark", "unknown"];

export const OWNER_UNOWNED = "unowned";
export const OWNER_ASSIGNED = "assigned";

/** `owner` is null (anyone), "unowned", "assigned", or one owner's email. */
export type SourceFilters = { states: HealthState[]; owner: string | null };

export const NO_FILTERS: SourceFilters = { states: [], owner: null };

/**
 * Reads `f_state` and `f_owner`. A hand-typed `f_state=dark` (a string, not a
 * list) still counts; an unknown state word is dropped, never matched.
 */
export function readSourceFilters(search: Record<string, unknown>): SourceFilters {
  const values = parseFilters(search);
  const raw = values.state;
  const picked = new Set(Array.isArray(raw) ? raw : raw ? [raw] : []);
  const owner = values.owner;
  return {
    states: FILTER_STATES.filter((s) => picked.has(s)),
    owner: typeof owner === "string" && owner ? owner : null,
  };
}

/** The filter values for `applyFilters`; an empty selection clears its key. */
export function filterValues(filters: SourceFilters): FilterValues {
  return { state: filters.states.length ? filters.states : null, owner: filters.owner };
}

/** The next search object for a filter change. Other params (`source`, another module's `f_`) survive. */
export function nextSearch(search: Record<string, unknown>, filters: SourceFilters): Record<string, unknown> {
  return applyFilters(search, filterValues(filters));
}

/**
 * The shared FilterBar owns the Owner pill and Clear; it hands back its values.
 * Clear sends `state: null` (listed in its `clears`), an owner pick keeps the chips.
 */
export function fromBarChange(current: SourceFilters, next: FilterValues): SourceFilters {
  return {
    states: next.state == null ? [] : current.states,
    owner: typeof next.owner === "string" && next.owner ? next.owner : null,
  };
}

/** Adds or removes one state, keeping chip order so the URL is stable. */
export function toggleState(states: HealthState[], state: HealthState): HealthState[] {
  const next = new Set(states);
  if (next.has(state)) next.delete(state);
  else next.add(state);
  return FILTER_STATES.filter((s) => next.has(s));
}

/** The state a row shows: unreadable health is "couldn't check" for every row (BR-001). */
export function shownState(source: Source, healthReadable: boolean): HealthState {
  return healthReadable ? source.health.state : "unknown";
}

export function matchesOwner(source: Source, owner: string | null): boolean {
  if (!owner) return true;
  if (owner === OWNER_UNOWNED) return !source.owner_email;
  if (owner === OWNER_ASSIGNED) return Boolean(source.owner_email);
  return source.owner_email === owner;
}

/** Status AND owner. No status chip picked means every status. */
export function filterSources(sources: Source[], filters: SourceFilters, healthReadable: boolean): Source[] {
  return sources.filter(
    (s) =>
      matchesOwner(s, filters.owner) &&
      (filters.states.length === 0 || filters.states.includes(shownState(s, healthReadable))),
  );
}

/**
 * Each chip's count: the rows that chip would add under the current owner
 * filter. The other status chips do not narrow it, so a count never reads 0
 * just because a different chip is on.
 */
export function stateCounts(sources: Source[], owner: string | null, healthReadable: boolean): Record<HealthState, number> {
  const c: Record<HealthState, number> = { ok: 0, drifting: 0, dark: 0, unknown: 0 };
  for (const s of sources) if (matchesOwner(s, owner)) c[shownState(s, healthReadable)] += 1;
  return c;
}

/**
 * Unowned, Assigned, then every owner present in the data, by name. An owner
 * named only by the URL (no rows left) is still listed, so the pill never
 * shows "All owners" while it is filtering.
 */
export function ownerOptions(sources: Source[], current: string | null): Array<{ value: string; label: string }> {
  const emails = new Set(sources.map((s) => s.owner_email).filter((e): e is string => Boolean(e)));
  if (current && current !== OWNER_UNOWNED && current !== OWNER_ASSIGNED) emails.add(current);
  return [
    { value: OWNER_UNOWNED, label: "Unowned" },
    { value: OWNER_ASSIGNED, label: "Assigned" },
    ...[...emails]
      .map((email) => ({ value: email, label: personLabel(email) }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];
}
